// "Is this still the account you mean?" — asked before a change to an account
// nobody has touched in a while.
//
// With several accounts open as tabs, the dangerous moment is coming back to
// one: after a break, a call, or a stretch in another tab, the screen in front
// of you may not be the customer you think it is, and the next click cancels
// someone's hosting. So once an open account has gone IDLE_MS without anyone
// interacting with it, its next write waits for a one-click confirmation that
// names the account.
//
// What counts as being in an account is any pointer, key or wheel input while
// that account is the one on screen. The gap is measured at that input, so the
// click that lands on "Save" after a long break is the one that marks the
// account as needing a check — it cannot refresh the timer and wave its own
// write through. The mark stays until someone confirms: looking around for a
// minute after coming back does not clear it, only saying yes does.
//
// Enforced at the fetch layer (gateAccountWrite, wired into staffAccess's
// guardedFetch by App), not per button: every write leaves through fetch, so a
// control nobody remembered to wire up is still covered. Reads are never held.
//
// sessionStorage, like the open-accounts list it shadows: a reload is not
// activity, so it must not reset the clock.

export const IDLE_MS = 15 * 60 * 1000;

const KEY = 'squish.crm.accountActivity';

// Writes that are not a change to the account on screen: the unlock exchange
// itself, the ~ console (scoped to every open account, and conversational),
// and live chat (a shopper's session, docked over every page, with a presence
// heartbeat no one clicks).
const UNGATED_PREFIXES = ['/api/otp/', '/api/console/', '/api/live-chat/'];
// And the one write that FOLLOWS money already moved: rep checkout records the
// order after Stripe has charged the card in the browser. Holding it — and a
// "Cancel" — would leave a charge with no order. The check still lands before
// the charge, on the intent call the checkout makes when it opens.
const UNGATED_PATHS = [/^\/api\/carts\/[^/]+\/rep-checkout\/complete$/];
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

function read() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function write(map) {
  try { sessionStorage.setItem(KEY, JSON.stringify(map)); } catch { /* the in-memory answer still holds for this page */ }
}

function entry(map, id) {
  const e = map[id];
  return e && Number.isFinite(e.at) ? e : null;
}

/** A fresh start: just opened, or just unlocked with the customer's code. */
export function noteAccountOpened(id, now = Date.now()) {
  if (!id) return;
  const map = read();
  map[id] = { at: now, stale: false };
  write(map);
}

/**
 * Someone interacted while this account was on screen. If it had been idle
 * IDLE_MS or more, it now needs confirming before its next write.
 */
export function noteAccountActivity(id, now = Date.now()) {
  if (!id) return;
  const map = read();
  const e = entry(map, id);
  // Unknown here (opened before this module shipped, or storage was cleared):
  // start its clock now rather than treat it as idle since the epoch.
  if (!e) { map[id] = { at: now, stale: false }; write(map); return; }
  const stale = e.stale === true || now - e.at >= IDLE_MS;
  // Writing on every keystroke is wasted work; once a second is plenty for a
  // 15-minute clock.
  if (stale === e.stale && now - e.at < 1000) return;
  map[id] = { at: now, stale };
  write(map);
}

/** Does a write to this account need the "right account?" check first? */
export function needsAccountConfirm(id, now = Date.now()) {
  if (!id) return false;
  const e = entry(read(), id);
  if (!e) return false;
  return e.stale === true || now - e.at >= IDLE_MS;
}

/** How long since anyone was in this account, in ms, or null if unknown. */
export function idleFor(id, now = Date.now()) {
  const e = entry(read(), id);
  return e ? Math.max(0, now - e.at) : null;
}

/** The staffer said yes: this is the account. */
export function confirmAccount(id, now = Date.now()) {
  noteAccountOpened(id, now);
}

/** The tab closed; nothing to remember. */
export function forgetAccountActivity(id) {
  const map = read();
  if (!(id in map)) return;
  delete map[id];
  write(map);
}

/** Is this request a change the check should hold? */
export function isGatedWrite(method, url) {
  if (SAFE_METHODS.has(String(method || 'GET').toUpperCase())) return false;
  let pathname;
  try {
    pathname = new URL(String(url), 'https://work.squish.co').pathname;
  } catch {
    return true;   // a write we cannot parse is not one to wave through
  }
  if (!pathname.startsWith('/api/')) return false;
  if (UNGATED_PATHS.some(re => re.test(pathname))) return false;
  return !UNGATED_PREFIXES.some(p => pathname.startsWith(p));
}

export const NOT_CONFIRMED_MESSAGE = 'Nothing was changed: the account was not confirmed.';

/**
 * Build the fetch gate. `onScreen()` names the account being looked at (or
 * null); `ask(id)` shows the check and resolves true (go ahead) or false.
 *
 * Returns a function for guardedFetch: null to send the request now, or a
 * promise of null (confirmed — send it) or of a local Response (declined).
 * Writes that arrive while the check is up — one click can send several —
 * wait on the same answer instead of stacking a second dialog.
 */
export function createAccountWriteGate({ onScreen, ask }) {
  let pending = null;   // { id, answer: Promise<boolean> }
  return function gateAccountWrite({ method, url }) {
    const id = onScreen();
    if (!id || !isGatedWrite(method, url) || !needsAccountConfirm(id)) return null;
    if (!pending || pending.id !== id) {
      const answer = Promise.resolve()
        .then(() => ask(id))
        .then(ok => ok === true, () => false)
        .then(ok => {
          if (ok) confirmAccount(id);
          if (pending?.answer === answer) pending = null;
          return ok;
        });
      pending = { id, answer };
    }
    return pending.answer.then(ok => (ok ? null : new Response(
      JSON.stringify({ error: NOT_CONFIRMED_MESSAGE, errorCode: 'ACCOUNT_NOT_CONFIRMED' }),
      { status: 409, headers: { 'Content-Type': 'application/json' } },
    )));
  };
}
